import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { AlertTriangle, Phone, FileText, Shield, Activity } from 'lucide-react';
import { motion } from 'framer-motion';

const EmergencyDashboard = () => {
    const { token } = useAuth();
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);

    // Fetch critical documents (Medical, Insurance, Identity)
    useEffect(() => {
        const fetchCriticalDocs = async () => {
            try {
                // Fetch all and filter client side for now, or add specific API params
                const res = await axios.get('http://localhost:4000/api/documents', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const critical = res.data.data.filter(d => 
                    ['Medical', 'Insurance', 'Personal & Identity', 'Legal'].includes(d.category)
                );
                setDocs(critical);
            } catch (error) {
                console.error("Failed to fetch documents");
            } finally {
                setLoading(false);
            }
        };
        fetchCriticalDocs();
    }, [token]);

    const categories = [
        { name: 'Medical Records', icon: Activity, color: 'text-red-600 bg-red-100', filter: 'Medical' },
        { name: 'Insurance Policies', icon: Shield, color: 'text-blue-600 bg-blue-100', filter: 'Insurance' },
        { name: 'Identity Docs', icon: FileText, color: 'text-purple-600 bg-purple-100', filter: 'Personal & Identity' },
    ];

    return (
        <Layout>
            <div className="max-w-7xl mx-auto space-y-8">
                {loading ? (
                     <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500"></div></div>
                ) : (
                <>
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-red-100 dark:bg-red-800 rounded-full">
                            <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-200" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-red-700 dark:text-red-400">Emergency Access Mode</h1>
                            <p className="text-red-600/80 dark:text-red-300">Quick access to critical information for first responders and family.</p>
                        </div>
                    </div>
                    <button className="px-6 py-3 bg-red-600 text-white font-bold rounded-xl shadow-lg hover:bg-red-700 transition flex items-center">
                        <Phone className="w-5 h-5 mr-2" />
                        Call Emergency Contacts
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {categories.map((cat, idx) => (
                        <motion.div 
                            key={cat.name}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: idx * 0.1 }}
                            className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700"
                        >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${cat.color}`}>
                                <cat.icon className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">{cat.name}</h3>
                            <ul className="space-y-3">
                                {docs.filter(d => d.category === cat.filter).slice(0, 3).map(doc => (
                                    <li key={doc._id} className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                                        <FileText className="w-4 h-4 mr-2 opacity-50" />
                                        <span className="truncate">{doc.title}</span>
                                    </li>
                                ))}
                                {docs.filter(d => d.category === cat.filter).length === 0 && (
                                    <li className="text-sm text-gray-400 italic">No documents found</li>
                                )}
                            </ul>
                        </motion.div>
                    ))}
                </div>
                </>
                )}
            </div>
        </Layout>
    );
};

export default EmergencyDashboard;
